import { isIRA } from '../../IRA/IRA-utility';
import {
  AGREEMENT_TYPES,
  FundNoteBuy,
  MONEY_MARKET_EVENT_GATE,
  MONEY_MARKET_EVENT_FEE,
  MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
  SystemCode,
} from 'src/settings/settings';
import {
  checkFoundFeaturesByCode,
  DISABLE_ON_MATCH,
  hasZeroBalance,
  isClosedFund,
  isTransactionRestrictedForFundingBank,
  isTransactionPendingStatusAgreements,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';
import { isAPP } from '../../advice/advice-utility';
export function updateBuyFundNotesForFund(
  fund,
  personalData,
  _data,
  selectedBankAgreementMap,
  bucketRoll = false
) {
  const { associatedPurchaseRestrictedLookup } = selectedBankAgreementMap;
  const {
    household: {
      householdBalanceTierStatus: { householdAssetTier = {} } = {},
    } = {},
  } = personalData;

  const isZeroValue = fund.balanceAsOf <= 0;
  const userSMPTier =
    householdAssetTier && householdAssetTier.id ? householdAssetTier.id : null;
  const checkForPsOrEpsTier = userSMPTier === 4 || userSMPTier === 5;
  // Bucket roll note
  updateFundNote(
    fund,
    isIRA(fund) && bucketRoll,
    FundNoteBuy.BUCKET_ROLL,
    DISABLE_ON_MATCH
  );
  updateFundNote(
    fund,
    isAPP(fund.productType) && fund.balanceAsOf === 0,
    FundNoteBuy.APP_ZERO_DOLLAR_BALANCE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED_BENE ||
      fund.agreementType === AGREEMENT_TYPES.ROTH_IRA_DECEASED_BENE,
    FundNoteBuy.INHERITED_FUND,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED ||
      fund.agreementType === AGREEMENT_TYPES.ROTH_IRA_DECEASED,
    FundNoteBuy.IRA_DECEASED,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.PENALTY_WITHOLDING),
    FundNoteBuy.PENALTY_WITHHOLDING,
    DISABLE_ON_MATCH
  );

  // If client has a fund they have closed out and is now closed to new investors,
  // Fund will only be disabled and note will be shown if the user is not in household tier to buyback
  // "Closed to New Investors" will be added to the notes section.
  updateFundNote(
    fund,
    isClosedFund(fund) &&
      hasZeroBalance(fund) &&
      !householdAssetTier.closedFundAccess &&
      fund.agreementType !== 'COLLEGE_SAVINGS',
    FundNoteBuy.CLOSED_NEW,
    DISABLE_ON_MATCH
  );

  // If client has a stop purchase on a fund, "Stop Purchase Applied" will be added to the notes section.
  // But not if stop is due to recent redemption, which has a separate note below
  // ManagedAccounts don't need to updateFund note for stop purchase here, it is being updated in (recurring-buy-fund-notes-utility.js)
  updateFundNote(
    fund,
    (fund.purchaseBlocked &&
      !fund.purchaseBlockRemovalDate &&
      fund.productType !== 'ManagedAccount') ||
      (fund.openToSubsequentInvestments === false &&
        fund.agreementType !== 'COLLEGE_SAVINGS'),
    FundNoteBuy.STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // If client has a fund that has a "Stop Mail" service in agreement level
  // checking addressChangeOnSameDay for edge case scenario (seen in prod)
  // "Address Undeliverable" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_MAIL_ID) &&
      !fund.addressChangedSameDay,
    FundNoteBuy.ADDRESS_UNDELIVERABLE,
    DISABLE_ON_MATCH
  );

  // If client has a fund under a registration that the client cannot associate the chosen bank to that fund,
  // "Cannot Link Bank Signature Required" will be added to the notes section. The popover message will have a
  // link that directs client to a form that opens in a new tab
  updateFundNote(
    fund,
    isTransactionRestrictedForFundingBank(fund, selectedBankAgreementMap),
    FundNoteBuy.CANNOT_LINK_BANK,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    !isTransactionRestrictedForFundingBank(fund, selectedBankAgreementMap) &&
      fund.bankAssociable &&
      isAPP(fund),
    FundNoteBuy.BANK_NOT_LINKED,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    isTransactionPendingStatusAgreements(fund, selectedBankAgreementMap),
    FundNoteBuy.PENDING_STATUS,
    DISABLE_ON_MATCH
  );

  // If client has a recent redemption of a fund within the past 30 days, "Recently Redeemed. Available on or after MM/YY"
  // will be added to the notes section. (Date is 30 days after the redemption date).
  updateFundNote(
    fund,
    fund.purchaseBlocked && fund.purchaseBlockRemovalDate,
    FundNoteBuy.RECENTLY_REDEEMED,
    DISABLE_ON_MATCH
  );

  // Closed Summit in IRA cannot reopen online, minimums
  updateFundNote(
    fund,
    isZeroValue && fund.summit && fund.displayClassification === 'RETIREMENT',
    FundNoteBuy.CANNOT_REOPEN_SUMMIT,
    DISABLE_ON_MATCH
  );

  // Closed iClass and not in personal service or enhanced personal service tiers cannot reopen online, minimums
  updateFundNote(
    fund,
    isZeroValue && fund.iClass && !checkForPsOrEpsTier,
    FundNoteBuy.CANNOT_REOPEN_ICLASS,
    DISABLE_ON_MATCH
  );

  // Closed iClass cannot reopen online, minimums
  updateFundNote(
    fund,
    associatedPurchaseRestrictedLookup[fund.uniqueKey],
    FundNoteBuy.ONLINE_PURCHASE_RESTRICTED,
    DISABLE_ON_MATCH
  );

  // Existing iClass
  updateFundNote(
    fund,
    !fund.newFund && fund.iClass,
    FundNoteBuy.EXISTING_ICLASS
  );

  // Fund is part of a pending transaction submitted using this account.
  updateFundNote(fund, fund.hasPendingTransaction, FundNoteBuy.PENDING);

  // If client has a fund with agreementType 'EDUCATION_SAVINGS_ACCOUNT'
  // "ESA-PLAN" will be added to the notes section.
  updateFundNote(
    fund,
    fund.agreementType === 'EDUCATION_SAVINGS_ACCOUNT',
    FundNoteBuy.ESA_PLAN,
    DISABLE_ON_MATCH
  );

  // Note added for RI2020 after noticing code in AAR that will prevent existing purchase when there
  // is a zero market value right after the account is opened.
  // updateFundNote(
  //   fund,
  //   fund.ticker === 'TRLAX' && fund.balanceAsOf === 0,
  //   FundNoteBuy.ONLINE_PURCHASE_RESTRICTED,
  //   DISABLE_ON_MATCH
  // );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_REMOVED
  );
}
